import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Box, Grid, Typography, Container, Modal, IconButton, Chip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PageBanner from '../components/PageBanner';
import SectionSidebar from '../components/SectionSidebar';
import CollectionsIcon from '@mui/icons-material/Collections';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import CelebrationIcon from '@mui/icons-material/Celebration';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface PhotoItem {
  id: string;
  title: string;
  imageUrl: string;
  category: string;
  order: number;
}

export default function Gallery() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [currentImage, setCurrentImage] = React.useState('');
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [currentImages, setCurrentImages] = React.useState<string[]>([]);
  const [firebasePhotos, setFirebasePhotos] = React.useState<PhotoItem[]>([]);
  const [photosLoading, setPhotosLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'gallery_photos'));
        const fetched: PhotoItem[] = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.imageUrl) {
            fetched.push({ id: docSnap.id, ...data } as PhotoItem);
          }
        });
        setFirebasePhotos(fetched.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      } catch (error) {
        console.error('Error loading gallery photos:', error);
      } finally {
        setPhotosLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  const photoUrls = firebasePhotos.map(p => p.imageUrl);

  const events = [
    { name: 'Vishu Celebration', img: `${process.env.PUBLIC_URL}/assets/slider_1.jpg` },
    { name: 'Annual Festival', img: `${process.env.PUBLIC_URL}/assets/slider_2.jpg` },
    { name: 'Onam Festivities', img: `${process.env.PUBLIC_URL}/assets/slider_6.jpg` },
  ];

  const premises = [
    { name: 'Main Temple', img: `${process.env.PUBLIC_URL}/assets/slide_3.png` },
    { name: 'Mandapam Hall', img: `${process.env.PUBLIC_URL}/assets/slider_1.jpg` },
    { name: 'Temple Entrance', img: `${process.env.PUBLIC_URL}/assets/slider_6.jpg` },
  ];

  const handleImageClick = (img: string, images: string[], index: number) => {
    setCurrentImage(img);
    setCurrentImages(images);
    setCurrentIndex(index);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => setPreviewOpen(false);
  const handlePrevImage = () => {
    const n = currentIndex > 0 ? currentIndex - 1 : currentImages.length - 1;
    setCurrentIndex(n);
    setCurrentImage(currentImages[n]);
  };
  const handleNextImage = () => {
    const n = currentIndex < currentImages.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(n);
    setCurrentImage(currentImages[n]);
  };

  const tabs = [
    { label: 'Photos', Icon: PhotoCameraIcon },
    { label: 'Videos', Icon: VideocamIcon },
    { label: 'Events', Icon: CelebrationIcon },
    { label: 'Premises', Icon: TempleHinduIcon },
  ];

  const tabRoutes = ['/gallery/photos', '/gallery/videos', '/gallery/events', '/gallery/premises'];

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO
        title="Temple Gallery — Kottayil Temple | ക്ഷേത്ര ഗ്യാലറി"
        description="Photos and images of Kottayil Temple (Kotta-Kovilakam Kshethram) — temple architecture, Kottayil Kulam, festivals, ceremonies, and sacred moments at Muchukunnu."
        path="/gallery"
        keywords="Kottayil temple gallery, Kottayil temple Muchukunnu photos, Muchukunnu temple photos, Kottayil kulam photos, Kottayil Shiva temple images, festival photos, Kerala temple gallery, Kozhikode temple photos, കൊട്ടയിൽ ക്ഷേത്രം ചിത്രങ്ങൾ, കൊട്ടയിൽ ക്ഷേത്രം മുച്ചുകുന്ന് ഫോട്ടോ"
      />
      <PageBanner title="Temple Gallery" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{ borderRadius: '24px', overflow: 'hidden', mb: 3.5, boxShadow: '0 20px 60px rgba(168,85,247,0.15)' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #581c87 0%, #a855f7 55%, #e9d5ff 100%)',
                px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 5, sm: 6 },
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  }}>
                    <CollectionsIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontSize: { xs: '1.8rem', sm: '2.4rem' }, mb: 1.5, lineHeight: 1.1 }}>
                    Explore Highlights
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.7, maxWidth: 500, mb: 2.5 }}>
                    Browse curated photos, videos, events &amp; temple premises in our gallery collection.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<CollectionsIcon sx={{ fontSize: 16, color: '#a855f7 !important' }} />} label="Media Gallery" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#a855f7', fontWeight: 700, fontSize: '0.7rem', height: 28, borderRadius: '14px' }} />
                    <Chip icon={<AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff !important' }} />} label="Curated Collection" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 28, borderRadius: '14px', backdropFilter: 'blur(10px)' }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* TAB PILLS */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {tabs.map((t, idx) => {
                  const tabGradients: [string, string][] = [
                    ['#ec4899', '#db2777'],
                    ['#ef4444', '#dc2626'],
                    ['#f59e0b', '#d97706'],
                    ['#10b981', '#059669'],
                  ];
                  return (
                    <Box
                      key={idx}
                      onClick={() => setTab(idx)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.8,
                        px: 2.5, py: 1, borderRadius: '14px', cursor: 'pointer',
                        fontWeight: 700, fontSize: '0.78rem',
                        transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                        ...(tab === idx
                          ? { background: `linear-gradient(135deg, ${tabGradients[idx][0]}, ${tabGradients[idx][1]})`, color: '#fff', boxShadow: `0 4px 16px ${tabGradients[idx][0]}40`, transform: 'scale(1.04)' }
                          : { bgcolor: '#fff', color: '#888', border: '1px solid rgba(0,0,0,0.06)', '&:hover': { bgcolor: '#f8f7f5', color: '#666', transform: 'translateY(-2px)' } }
                        ),
                      }}
                    >
                      <t.Icon sx={{ fontSize: 16 }} />
                      {t.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* PHOTOS TAB */}
            {tab === 0 && (
              photosLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#a855f7' }} />
                </Box>
              ) : firebasePhotos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fff', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <PhotoCameraIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography sx={{ color: '#999', fontWeight: 600 }}>No photos uploaded yet</Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {firebasePhotos.map((photo, i) => (
                    <Grid item xs={6} sm={4} key={photo.id}>
                      <Box
                        onClick={() => handleImageClick(photo.imageUrl, photoUrls, i)}
                        sx={{
                          borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                          position: 'relative',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                          transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.12)', '& .imgOverlay': { opacity: 1 } },
                        }}
                      >
                        <Box component="img" src={photo.imageUrl.startsWith('data:') || photo.imageUrl.startsWith('http') ? photo.imageUrl : `${process.env.PUBLIC_URL}${photo.imageUrl}`} alt={photo.title} sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                        <Box className="imgOverlay" sx={{
                          position: 'absolute', inset: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)',
                          opacity: 0, transition: 'opacity 300ms ease',
                          display: 'flex', alignItems: 'flex-end', p: 1.5,
                        }}>
                          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}>{photo.title}</Typography>
                        </Box>
                        <Box sx={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PhotoCameraIcon sx={{ fontSize: 13, color: '#fff' }} />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )
            )}

            {/* VIDEOS TAB */}
            {tab === 1 && (
              <Grid container spacing={2.5}>
                {[
                  { title: 'Temple Virtual Tour', url: 'https://www.youtube.com/embed/bFHz3KocQc0' },
                  { title: 'Temple Documentary', url: 'https://www.youtube.com/embed/bFHz3KocQc0' },
                ].map((v, i) => {
                  const vGradients: [string, string][] = [['#ef4444', '#dc2626'], ['#8b5cf6', '#7c3aed']];
                  const gr = vGradients[i % vGradients.length];
                  return (
                    <Grid item xs={12} key={i}>
                      <Box sx={{
                        bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                        transition: 'all 350ms ease',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 36px rgba(0,0,0,0.08)' },
                      }}>
                        {/* Gradient header */}
                        <Box sx={{
                          background: `linear-gradient(135deg, ${gr[0]}, ${gr[1]})`,
                          px: { xs: 2, sm: 2.5 }, py: 1.5,
                          display: 'flex', alignItems: 'center', gap: 1.5,
                        }}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '10px',
                            background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                            border: '1.5px solid rgba(255,255,255,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <VideocamIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff' }}>{v.title}</Typography>
                        </Box>
                        <Box component="iframe" src={v.url} title={v.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          sx={{ width: '100%', height: { xs: 250, sm: 380 }, border: 'none', display: 'block' }}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* EVENTS TAB */}
            {tab === 2 && (
              <Grid container spacing={2}>
                {events.map((event, i) => (
                  <Grid item xs={12} sm={4} key={i}>
                    <Box
                      onClick={() => handleImageClick(event.img, events.map(e => e.img), i)}
                      sx={{
                        borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                        position: 'relative',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                        transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.12)' },
                      }}
                    >
                      <Box component="img" src={event.img} alt={event.name} sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                        px: 2, py: 1.5,
                      }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.82rem', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                          {event.name}
                        </Typography>
                      </Box>
                      <Box sx={{ position: 'absolute', top: 10, left: 10, px: 1.2, py: 0.3, borderRadius: '10px', background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                        <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', textTransform: 'uppercase' }}>Event</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* PREMISES TAB */}
            {tab === 3 && (
              <Grid container spacing={2}>
                {premises.map((area, i) => (
                  <Grid item xs={12} sm={4} key={i}>
                    <Box
                      onClick={() => handleImageClick(area.img, premises.map(p => p.img), i)}
                      sx={{
                        borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                        position: 'relative',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                        transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.12)' },
                      }}
                    >
                      <Box component="img" src={area.img} alt={area.name} sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                        px: 2, py: 1.5,
                      }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.82rem' }}>
                          {area.name}
                        </Typography>
                      </Box>
                      <Box sx={{ position: 'absolute', top: 10, left: 10, px: 1.2, py: 0.3, borderRadius: '10px', background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                        <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', textTransform: 'uppercase' }}>Premises</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            <Box sx={{ mt: 3.5, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
              <Box
                onClick={() => navigate(tabRoutes[tab])}
                sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                  px: 3, py: 1.2, borderRadius: '14px', cursor: 'pointer',
                  fontWeight: 800, fontSize: '0.82rem', color: '#fff',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  boxShadow: '0 8px 22px rgba(124,58,237,0.28)',
                  transition: 'all 250ms ease',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 28px rgba(124,58,237,0.35)' },
                }}
              >
                View More {tabs[tab].label}
                <NavigateNextIcon sx={{ fontSize: 18 }} />
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="gallery" activePath="/gallery" />
          </Grid>
        </Grid>
      </Container>

      {/* Image Preview Modal */}
      <Modal open={previewOpen} onClose={handleClosePreview} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ position: 'relative', maxWidth: { xs: '95vw', sm: '90vw' }, maxHeight: '85dvh', outline: 'none' }} onClick={(e) => e.stopPropagation()}>
          <IconButton onClick={handleClosePreview} sx={{
            position: 'absolute', top: -50, right: 0, color: '#fff',
            bgcolor: 'rgba(168,85,247,0.9)', '&:hover': { bgcolor: '#a855f7', transform: 'scale(1.1)' },
            zIndex: 1, transition: 'all 200ms ease',
          }}>
            <CloseIcon />
          </IconButton>

          {currentImages.length > 1 && (
            <IconButton onClick={handlePrevImage} sx={{
              position: 'absolute', left: { xs: 8, md: -60 }, top: '50%', transform: 'translateY(-50%)',
              color: '#fff', bgcolor: 'rgba(168,85,247,0.9)',
              '&:hover': { bgcolor: '#a855f7', transform: 'translateY(-50%) scale(1.1)' },
              transition: 'all 200ms ease',
            }}>
              <NavigateBeforeIcon sx={{ fontSize: 32 }} />
            </IconButton>
          )}

          <Box component="img" src={currentImage} alt="Preview" sx={{
            maxWidth: '100%', maxHeight: '85dvh', borderRadius: '12px',
            border: '3px solid rgba(168,85,247,0.6)', boxShadow: '0 20px 60px rgba(0,0,0,0.8)', display: 'block',
          }} />

          {currentImages.length > 1 && (
            <IconButton onClick={handleNextImage} sx={{
              position: 'absolute', right: { xs: 8, md: -60 }, top: '50%', transform: 'translateY(-50%)',
              color: '#fff', bgcolor: 'rgba(168,85,247,0.9)',
              '&:hover': { bgcolor: '#a855f7', transform: 'translateY(-50%) scale(1.1)' },
              transition: 'all 200ms ease',
            }}>
              <NavigateNextIcon sx={{ fontSize: 32 }} />
            </IconButton>
          )}

          {currentImages.length > 1 && (
            <Box sx={{
              position: 'absolute', bottom: -40, left: '50%', transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
              color: '#fff', px: 2.5, py: 0.8, borderRadius: '12px',
              fontWeight: 700, fontSize: '0.82rem', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}>
              {currentIndex + 1} / {currentImages.length}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
